import { CARTOPOUSSE_BASE_URL } from '../utils/ApiUrls';

class RiskService {
  // Create a new risk with its actions
  async createRisk(riskData, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/risks/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(riskData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Risk creation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Fetch all risks for the user's organization
  async getOrganizationRisks(accessToken, filters = {},  pagination_data) {
    try {
      // console.log(' riskService.getOrganizationRisks - Received filters:', filters);
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.risk_name) {
        queryParams.append('risk_name', filters.risk_name);
      }
      if (filters.assigned_user) {
        queryParams.append('assigned_user', filters.assigned_user);
      }
      if (filters.probability) {
        queryParams.append('probability', filters.probability);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if(pagination_data.page){
        queryParams.append('page', pagination_data.page)
      }

      if(pagination_data.page_size){
        queryParams.append('page_size', pagination_data.page_size)
      }

      const queryString = queryParams.toString();
      const url = `${CARTOPOUSSE_BASE_URL}/risks/${queryString ? `?${queryString}` : ''}`;
      
      // console.log(' riskService.getOrganizationRisks - Final URL:', url);
      // console.log(' riskService.getOrganizationRisks - Query params:', queryString);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching organization risks',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Fetch risks created by the current user
  async getMyRisks(accessToken, filters = {}, pagination_data) {
    try {
      // console.log('📡 riskService.getMyRisks - Received filters:', filters);
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.risk_name) {
        queryParams.append('risk_name', filters.risk_name);
      }
      if (filters.assigned_user) {
        queryParams.append('assigned_user', filters.assigned_user);
      }
      if (filters.probability) {
        queryParams.append('probability', filters.probability);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if(pagination_data.page){
        queryParams.append('page', pagination_data.page)
      }

      if(pagination_data.page_size){
        queryParams.append('page_size', pagination_data.page_size)
      }

      const queryString = queryParams.toString();
      const url = `${CARTOPOUSSE_BASE_URL}/risks/my-risks/${queryString ? `?${queryString}` : ''}`;
      
      // console.log('📡 riskService.getMyRisks - Final URL:', url);
      // console.log('📡 riskService.getMyRisks - Query params:', queryString);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching your risks',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Fetch details for a single risk
  async getRiskDetails(riskId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/risks/${riskId}/`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching risk details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Edit/Update risk
  async editRisk(riskId, riskData, accessToken) {
    try {
        const response = await fetch(`${CARTOPOUSSE_BASE_URL}/risks/${riskId}/edit/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(riskData)
        });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Risk update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Alias for updateRisk to maintain compatibility
  async updateRisk(riskId, riskData, accessToken) {
    return this.editRisk(riskId, riskData, accessToken);
  }

  // Validate risk (change status)
  async validateRisk(riskId, status, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/risks/${riskId}/validate/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Risk validation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // update risk probability
  async updateRiskProbability(riskId, newProbability, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/risks/${riskId}/update-probability/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ newProbability }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Risk update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Delete risk
  async deleteRisk(riskId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/risks/${riskId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error deleting risk',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Search risks for autocomplete functionality (used in activity forms)
  async searchRisks(query, accessToken) {
    try {
      console.log('📡 riskService.searchRisks - Query:', query);
      
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/risks/search/?q=${encodeURIComponent(query)}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error searching risks',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }




  // -------------------Update activity risk--------------------
    async updateActivityRisk(riskId, riskData, accessToken) {
    try {
        const response = await fetch(`${CARTOPOUSSE_BASE_URL}/risks/update-activity-risk/${riskId}/edit/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(riskData)
        });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Risk update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }
}

const riskService = new RiskService();
export default riskService;
